import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Route, Routes } from 'react-router-dom';

import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import AppTabs from '../../../components/designe/app-tabs/AppTabs';
import PageContainer from '../../../components/layout/page-container/PageContainer';
import { useRegisterPageContainerError } from '../../../components/layout/page-container/page-container-error-handler';
import AppNavigate from '../../../components/reusable/app-navigate/AppNavigate';
import ConfirmDialog from '../../../components/reusable/confirm-dialog/ConfirmDialog';
import SortTrainingSetsModal from '../../../components/reusable/sort-training-sets-modal/SortTrainingSetsModal';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import withRouter from '../../../hoc/with-router';
import { useNotFound } from '../../../hooks/useNotFound';
import { TrainingStatus } from '../../../shared/constants';
import { Paths } from '../../../shared/paths';
import { EditTrainingSetModal } from '../current-training/edit-training-set-modal/EditTrainingSetModal';
import EditTrainingModal from '../edit-training-modal/EditTrainingModal';
import { useStyles } from './FinishedTraining.styles';
import FinishedTrainingAccess from './finished-training-access/FinishedTrainingAccess';
import FinishedTrainingStats from './finished-training-stats/FinishedTrainingStats';
import FinishedTrainingSummary from './finished-training-summary/FinishedTrainingSummary';
import { useFinishedTrainingPage } from './useFinishedTrainingPage';

function FinishedTraining(props) {
  const { params } = props;
  const { trainingId } = params;
  const { t } = useTranslation();
  const { navigate } = useNavigationStack();

  const theme = useTheme();

  const { classes } = useStyles(theme);
  const {
    training,
    trainingSetGroups,
    exerciseOptions,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
    trainingEditing,
    setTrainingEditing,
    showDeleteConfirmation,
    setShowDeleteConfirmation,
    deleteTraining,
    isDeletingTraining,
    editedTrainingSetGroup,
    setEditedTrainingSetGroup,
    onEditTrainingSetSubmit,
    onEditTrainingSetClick,
    onNewTrainingSetClick,
    sortSetsModalOpen,
    setSortSetsModalOpen,
    onSaveSetsOrder,
    isSavingTrainingSets,
    isSavingSetsOrder,
    orderedTrainingSets,
  } = useFinishedTrainingPage(trainingId);
  const handleRetry = useCallback(() => {
    void refetch();
  }, [refetch]);
  useRegisterPageContainerError({
    errors: isError && error ? [error] : [],
    isLoading: !training && (isLoading || isFetching),
    hasData: Boolean(training),
    onRetry: handleRetry,
  });

  useNotFound(!isFetching && !training);

  useEffect(() => {
    if (!training) return;

    if (training.status !== TrainingStatus.finished) {
      navigate(Paths.trainings.toViewId(trainingId), {
        replace: true,
      });
    }
  }, [training]);

  const tabsConfig = useMemo(() => {
    const tabs = [
      {
        label: t('trainings.summary'),
        path: Paths.trainings.toFinishedSummaryId(trainingId),
      },
      {
        label: t('trainings.stats'),
        path: Paths.trainings.toFinishedStatsId(trainingId),
      },
    ];
    if (training?.permissions?.canConfigureAccess) {
      tabs.push({
        label: t('trainings.finishedAccessTab'),
        path: Paths.trainings.toFinishedAccessId(trainingId),
      });
    }
    return tabs;
  }, [trainingId, t, training?.permissions?.canConfigureAccess]);

  return (
    <PageContainer
      title={t('trainings.finishedTraining')}
      backButton
      onRefresh={() => void refetch()}>
      <AppTabs config={tabsConfig} />

      <Box marginTop={1} className={classes.trainingViewContent}>
        <Routes>
          <Route
            path={Paths.trainings.summary}
            element={
              <FinishedTrainingSummary
                training={training}
                trainingSetGroups={trainingSetGroups}
                exerciseOptions={exerciseOptions}
                onEditClick={() => setTrainingEditing(true)}
                onDeleteClick={() => setShowDeleteConfirmation(true)}
                onSortClick={() => setSortSetsModalOpen(true)}
                onEditSetGroupClick={onEditTrainingSetClick}
                onAddTrainingSetClick={onNewTrainingSetClick}
              />
            }
          />
          <Route
            path={Paths.trainings.stats}
            element={<FinishedTrainingStats training={training} />}
          />
          <Route
            path={Paths.trainings.access}
            element={
              <FinishedTrainingAccess
                training={training}
                trainingId={trainingId}
                isFetching={isFetching}
              />
            }
          />
          <Route
            path={'*'}
            element={
              <AppNavigate
                to={Paths.trainings.toFinishedSummaryId(trainingId)}
                replace
              />
            }
          />
        </Routes>
      </Box>
      <EditTrainingModal
        isEditTraining={trainingEditing && !!training?.permissions?.canEditInfo}
        editedTraining={training}
        finishDataEditable={!!training?.permissions?.canEdit}
        onCancel={() => setTrainingEditing(false)}
      />
      <EditTrainingSetModal
        trainingSetGroup={editedTrainingSetGroup}
        setTrainingSetGroup={setEditedTrainingSetGroup}
        onSubmit={onEditTrainingSetSubmit}
        isSavingTrainingSets={isSavingTrainingSets}
        trainingOwnerId={training?.ownerId}
        showSetItemStatus
        trainingOptionValueField="value"
        trainingOptionValueField2="value2"
        copyLastTrainingSetItemStatusOnAdd
      />
      <SortTrainingSetsModal
        open={sortSetsModalOpen}
        onClose={() => setSortSetsModalOpen(false)}
        trainingSets={orderedTrainingSets}
        onSave={onSaveSetsOrder}
        isSaving={isSavingSetsOrder}
      />

      <ConfirmDialog
        open={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        title={t('common.delete')}
        onSubmit={() => deleteTraining()}
        isLoading={isDeletingTraining}>
        {t('common.deleteQuestion')}
      </ConfirmDialog>
    </PageContainer>
  );
}

export default withRouter(FinishedTraining);
